import React from 'react';
import { Crown, Calendar, Infinity } from 'lucide-react';
import { tokenResetLabel, isFreeLikePlan, displayPlanName } from '../../utils/planCatalogUtils';

const StatusCards = ({ subscription, getUsagePercentage, getRemainingRequests }) => {
  const normalizeBillingCycle = (cycle) => {
    const normalized = String(cycle || 'monthly').toLowerCase().trim();
    if (['year', 'yearly', 'annual', 'annually', '12m', '12_month'].includes(normalized)) {
      return 'yearly';
    }
    return 'monthly';
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (typeof value === 'number') {
      const normalized = value < 1e12 ? value * 1000 : value;
      const dt = new Date(normalized);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (/^\d+$/.test(trimmed)) return parseDateValue(Number(trimmed));
      const dt = new Date(trimmed);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
    return null;
  };

  const isReasonableBillingDate = (dateValue) => {
    if (!dateValue) return false;
    const now = new Date();
    const upperBound = new Date(now);
    upperBound.setFullYear(now.getFullYear() + 2);
    return dateValue <= upperBound;
  };

  const resolveBillingCardData = () => {
    const status = subscription?.status?.toLowerCase();
    if (status === 'cancelled') {
      const endDate = parseDateValue(subscription?.end_date);
      return { title: endDate ? endDate.toLocaleDateString() : 'N/A', subtitle: 'Subscription ends' };
    }
    const price = Number(subscription?.price ?? 0);
    const freeLike = isFreeLikePlan(
      subscription?.plan_name,
      subscription?.plan_type,
      subscription?.is_free_trial
    );
    if (freeLike || price <= 0) {
      const trialDays = Number(subscription?.trial_days);
      const daysLeft = Number(subscription?.trial_days_remaining);
      if (Number.isFinite(daysLeft) && daysLeft >= 0) {
        return {
          title: `${Math.floor(daysLeft)}d left`,
          subtitle:
            Number.isFinite(trialDays) && trialDays > 0
              ? `Free trial (${trialDays} days)`
              : 'Free / Community',
        };
      }
      return { title: 'N/A', subtitle: 'No billing date' };
    }

    const billingCycle = normalizeBillingCycle(subscription?.billing_cycle);
    const nextBillingDate = parseDateValue(subscription?.next_billing_date);
    const startDate = parseDateValue(subscription?.start_date);

    if (nextBillingDate && billingCycle === 'yearly') {
      if (startDate) {
        const expected = new Date(startDate);
        expected.setFullYear(expected.getFullYear() + 1);
        const diffDays = Math.abs((nextBillingDate - expected) / (1000 * 60 * 60 * 24));
        if (diffDays > 45 && isReasonableBillingDate(expected)) {
          return { title: expected.toLocaleDateString(), subtitle: 'Next annual billing' };
        }
      }
      if (isReasonableBillingDate(nextBillingDate)) {
        return { title: nextBillingDate.toLocaleDateString(), subtitle: 'Next annual billing' };
      }
    }

    if (nextBillingDate && isReasonableBillingDate(nextBillingDate)) {
      return { title: nextBillingDate.toLocaleDateString(), subtitle: 'Next billing date' };
    }
    return { title: 'N/A', subtitle: 'Billing date pending' };
  };

  const billingCardData = resolveBillingCardData();
  const billingCycleLabel = normalizeBillingCycle(subscription?.billing_cycle);
  const priceSuffix = billingCycleLabel === 'yearly' ? '/yr' : '/mo';
  const priceBadge = (() => {
    const p = Number(subscription?.price ?? 0);
    if (!Number.isFinite(p) || p <= 0) return '$0.00';
    return `$${p.toFixed(2)}${priceSuffix}`;
  })();

  const getStatusBadge = () => {
    const status = subscription?.status?.toLowerCase();
    if (status === 'cancelled') return { text: 'Cancelled', variant: 'warning' };
    if (status === 'expired' || subscription?.trial_expired === true) {
      return { text: 'Expired', variant: 'danger' };
    }
    if (status === 'active' || status === 'trial' || status === 'trialing') {
      return { text: 'Active', variant: 'success' };
    }
    return { text: subscription?.status || 'Active', variant: 'neutral' };
  };

  const statusBadge = getStatusBadge();
  const remainingValue = getRemainingRequests();
  const formattedRemainingValue = (() => {
    if (remainingValue === null || remainingValue === undefined) return 'N/A';
    if (typeof remainingValue === 'string') return remainingValue;
    const numericValue = Number(remainingValue);
    if (Number.isNaN(numericValue)) return String(remainingValue);
    return numericValue.toLocaleString();
  })();

  const cards = [
    {
      id: 'current-plan',
      icon: Crown,
      title: displayPlanName(subscription?.plan_name, 'No plan'),
      subtitle: 'Current plan',
      badge: statusBadge.text,
      badgeVariant: statusBadge.variant,
      accent: 'candor-metric--blue',
    },
    {
      id: 'billing',
      icon: Calendar,
      title: billingCardData.title,
      subtitle: billingCardData.subtitle,
      badge: priceBadge,
      badgeVariant: 'info',
      accent: 'candor-metric--teal',
    },
    {
      id: 'remaining',
      icon: Infinity,
      title: formattedRemainingValue,
      subtitle: 'Remaining tokens',
      badge: tokenResetLabel(subscription),
      badgeVariant: 'neutral',
      accent: 'candor-metric--slate',
    },
  ];

  return (
    <div className="candor-metrics-grid">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <article key={card.id} className={`candor-metric-card ${card.accent}`}>
            <div className="candor-metric-icon">
              <Icon className="w-5 h-5" strokeWidth={2} />
            </div>
            <div className="candor-metric-body">
              <p className="candor-metric-label">{card.subtitle}</p>
              <h3 className="candor-metric-value">{card.title}</h3>
              <span className={`candor-status-pill candor-status-pill--${card.badgeVariant}`}>
                {card.badge}
              </span>
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default StatusCards;
